import Phaser from 'phaser';
import { ConveyorSystem } from '../systems/ConveyorSystem';

const NUM_CONVEYOR_DOTS = 32;

/** Padding around path bounds for the widest stroke (60px / 2) plus shadow offset. */
const BELT_TEX_PADDING = 34;
/** Unique key for the generated belt texture (reused across resets). */
const BELT_TEXTURE_KEY = '__conveyor_belt__';

/**
 * Owns all conveyor-related visuals: the layered belt path,
 * animated flow dots, speed glow, and the "conveyor full" flash.
 *
 * GameScene delegates conveyor drawing and per-frame dot updates to this class.
 */
export class ConveyorRenderer {
  private scene: Phaser.Scene;
  private conveyor: ConveyorSystem;

  // Batched belt sprite (replaces the old persistent Graphics object)
  private beltSprite: Phaser.GameObjects.Image | null = null;

  // Flow dots
  private conveyorDots: Phaser.GameObjects.Image[] = [];
  private conveyorDotTs: number[] = [];

  // Flash state
  conveyorFlashActive = false;

  constructor(scene: Phaser.Scene, conveyor: ConveyorSystem) {
    this.scene = scene;
    this.conveyor = conveyor;
  }

  /** Reset mutable state (called from GameScene.init). */
  reset(): void {
    this.conveyorDots = [];
    this.conveyorDotTs = [];
    this.conveyorFlashActive = false;
    this.beltSprite = null;
    // Remove stale generated texture so it can be re-created for the new level
    if (this.scene.textures.exists(BELT_TEXTURE_KEY)) {
      this.scene.textures.remove(BELT_TEXTURE_KEY);
    }
  }

  // ── Drawing ──────────────────────────────────────────────────────────

  drawConveyorPath(): void {
    const path = this.conveyor.getPath();

    // ── Compute tight bounding box around the path ──────────────────
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const pt of path) {
      if (pt.x < minX) minX = pt.x;
      if (pt.y < minY) minY = pt.y;
      if (pt.x > maxX) maxX = pt.x;
      if (pt.y > maxY) maxY = pt.y;
    }
    // Expand for stroke widths and shadow offset
    const ox = minX - BELT_TEX_PADDING;
    const oy = minY - BELT_TEX_PADDING;
    const tw = maxX - minX + BELT_TEX_PADDING * 2;
    const th = maxY - minY + BELT_TEX_PADDING * 2 + 2; // +2 for shadow y-offset

    // ── Draw all static belt layers into a temporary Graphics ───────
    const g = this.scene.add.graphics();

    // Translate path coordinates so they're relative to the texture origin
    const offsetPath = path.map(p => ({ x: p.x - ox, y: p.y - oy }));

    // Outer shadow / depth
    g.lineStyle(60, 0x000000, 0.2);
    g.beginPath();
    g.moveTo(offsetPath[0].x, offsetPath[0].y + 4);
    for (let i = 1; i < offsetPath.length; i++) g.lineTo(offsetPath[i].x, offsetPath[i].y + 4);
    g.closePath();
    g.strokePath();

    // Belt fill — dark track, wide and solid
    g.lineStyle(44, 0x22223a, 0.9);
    g.beginPath();
    g.moveTo(offsetPath[0].x, offsetPath[0].y);
    for (let i = 1; i < offsetPath.length; i++) g.lineTo(offsetPath[i].x, offsetPath[i].y);
    g.closePath();
    g.strokePath();

    // Inner highlight (lighter edge on top)
    g.lineStyle(28, 0x3a3a5a, 0.45);
    g.beginPath();
    g.moveTo(offsetPath[0].x, offsetPath[0].y);
    for (let i = 1; i < offsetPath.length; i++) g.lineTo(offsetPath[i].x, offsetPath[i].y);
    g.closePath();
    g.strokePath();

    // Bright center line (the rail)
    g.lineStyle(4, 0x7777aa, 0.5);
    g.beginPath();
    g.moveTo(offsetPath[0].x, offsetPath[0].y);
    for (let i = 1; i < offsetPath.length; i++) g.lineTo(offsetPath[i].x, offsetPath[i].y);
    g.closePath();
    g.strokePath();

    // Corner rollers — small bright circles at regular intervals
    const totalLen = this.conveyor.getTotalLength();
    const rollerSpacing = 70;
    const numRollers = Math.floor(totalLen / rollerSpacing);
    for (let i = 0; i < numRollers; i++) {
      const t = i / numRollers;
      const pos = this.conveyor.getPositionAtT(t);
      g.fillStyle(0x555577, 0.5);
      g.fillCircle(pos.x - ox, pos.y - oy, 7);
      g.fillStyle(0x8888aa, 0.4);
      g.fillCircle(pos.x - ox, pos.y - oy, 3.6);
    }

    // ── Bake into a texture and display as a single sprite ──────────
    if (this.scene.textures.exists(BELT_TEXTURE_KEY)) {
      this.scene.textures.remove(BELT_TEXTURE_KEY);
    }
    g.generateTexture(BELT_TEXTURE_KEY, tw, th);
    g.destroy();

    this.beltSprite = this.scene.add.image(ox, oy, BELT_TEXTURE_KEY);
    this.beltSprite.setOrigin(0, 0);
  }

  createConveyorDots(): void {
    for (let i = 0; i < NUM_CONVEYOR_DOTS; i++) {
      const t = i / NUM_CONVEYOR_DOTS;
      const pos = this.conveyor.getPositionAtT(t);
      const dot = this.scene.add.image(pos.x, pos.y, 'conveyor-dot').setAlpha(0.4);
      this.conveyorDots.push(dot);
      this.conveyorDotTs.push(t);
    }
  }

  updateConveyorDots(dt: number): void {
    const speed = this.conveyor.getSpeed();
    const totalLen = this.conveyor.getTotalLength();
    const tDelta = (speed * dt) / totalLen;

    for (let i = 0; i < this.conveyorDots.length; i++) {
      this.conveyorDotTs[i] = (this.conveyorDotTs[i] + tDelta) % 1;
      const pos = this.conveyor.getPositionAtT(this.conveyorDotTs[i]);
      this.conveyorDots[i].setPosition(pos.x, pos.y);
    }
  }

  addConveyorSpeedGlow(): void {
    const path = this.conveyor.getPath();
    const glow = this.scene.add.graphics();

    glow.lineStyle(16, 0x66aaff, 0.4);
    glow.beginPath();
    glow.moveTo(path[0].x, path[0].y);
    for (let i = 1; i < path.length; i++) glow.lineTo(path[i].x, path[i].y);
    glow.closePath();
    glow.strokePath();

    glow.setBlendMode(Phaser.BlendModes.ADD);

    // Pulse the glow
    this.scene.tweens.add({
      targets: glow,
      alpha: { from: 0.4, to: 0.8 },
      duration: 400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /** Flash the conveyor path red to indicate it's full. */
  flashConveyorFull(): void {
    if (this.conveyorFlashActive) return;
    this.conveyorFlashActive = true;

    const path = this.conveyor.getPath();
    const g = this.scene.add.graphics();

    g.lineStyle(20, 0xff3333, 0.6);
    g.beginPath();
    g.moveTo(path[0].x, path[0].y);
    for (let i = 1; i < path.length; i++) g.lineTo(path[i].x, path[i].y);
    g.closePath();
    g.strokePath();

    g.setBlendMode(Phaser.BlendModes.ADD);

    this.scene.tweens.add({
      targets: g,
      alpha: 0,
      duration: 300,
      ease: 'Power2',
      onComplete: () => {
        g.destroy();
        this.conveyorFlashActive = false;
      },
    });
  }

  /** Clean up references. Called on scene shutdown. */
  destroy(): void {
    this.conveyorDots = [];
    this.conveyorDotTs = [];
    this.conveyorFlashActive = false;
    if (this.beltSprite) {
      this.beltSprite.destroy();
      this.beltSprite = null;
    }
    if (this.scene.textures.exists(BELT_TEXTURE_KEY)) {
      this.scene.textures.remove(BELT_TEXTURE_KEY);
    }
  }
}
